/** Most of the complicated stuff in this file is to work around known bugs and limitations of 3rd-party libraries.
    It would be great to delete a lot of code here AFTER the 3rd-party libs are better able to handle things.
    Also there is an important clarification of the terms "overlap" and "intersect" which calling code relies on.
    "Overlap" is defined as a shared area between two polygons, and not merely a single shared vertex or
    line segment which meets the superset criteria for "intersect". */
import { get } from 'lodash';

// 3rd-party wrapped
import { DEFAULT_CLIPPING_LIBRARY, clippingLibrary } from './constants';
import { area, intersect as turfIntersect, lineIntersect } from '3rd-party-wrappers/turf';
import { intersection as martinezIntersect } from '3rd-party-wrappers/martinez-polygon-clipping';
import { intersect as clipperLibIntersect } from '3rd-party-wrappers/clipper-lib';
import { intersect as cglIntersect } from '@tcc/cgl';

// util
import { mergeIntersectingPolygonsInArray } from 'geometry/geojson/union';
import { isValidXPolygon } from 'geometry/geojson/validator';
import { doesXPolygonHaveArea } from 'geometry/geojson/area';
import { roundGeometry } from 'util/round';

import {
  coordinatesToXPolygon,
  geometryCollectionToArray,
  getCoordinates,
  getGeometry,
  multiPolygonToPolygonArray,
  polygonArrayToXPolygon,
  xPolygonToPolygonArray,
  getFeature
} from 'geometry/geojson/convert';
import { logApiError } from '3rd-party-wrappers/guarded-call';
import { workerLogError } from 'scripts/editZones.worker';

const _calculateOverlapPolygonsMartinez = (polygon1, polygon2) => {
  try {
    const coordinates1 = roundGeometry(getCoordinates(polygon1));
    const coordinates2 = roundGeometry(getCoordinates(polygon2));
    const intersectCoordinates = martinezIntersect(coordinates1, coordinates2);
    if (intersectCoordinates === undefined) { return undefined; } // Guarded call failed.

    // Check for "no overlap" case.
    if (intersectCoordinates === null || !intersectCoordinates.length) { return null; } // Success.

    return coordinatesToXPolygon(intersectCoordinates);
  } catch(error) {
    workerLogError({error, context:{polygon1, polygon2}});
    return undefined;
  }
};

/** @return Polygon or MultiPolygon containing overlap of geoJson1/2. Or null, if no overlap.
   Or undefined, if an error, in which case details about the error will already be logged. (guarded style) */
const _calculateOverlapMartinez = (geometry1, geometry2) => {
  try {
    // Split MultiPolygons into individual polygons to get around a Martinez error.
    const resultPolygons = xPolygonToPolygonArray(geometry1);
    const clipPolygons = xPolygonToPolygonArray(geometry2);
    let intersections = [];

    // For each result polygon/clipping polygon combination, collect its intersection.
    let fatalError = false;
    resultPolygons.forEach((resultPolygon) => {
      if (fatalError) { return; }
      clipPolygons.forEach((clipPolygon) => {
        if (fatalError) { return; }

        const i = _calculateOverlapPolygonsMartinez(resultPolygon, clipPolygon);
        if (!i) { fatalError = (i === undefined); return; } // null = no intersection, undefined = error

        /* If returned a MultiPolygon (possible for 2 polygon params with inner rings) split it into polygons before
           adding to intersections. */
        if (i.type === 'MultiPolygon') {
          intersections = intersections.concat(multiPolygonToPolygonArray(i));
        } else {
          intersections.push(i);
        }
      });
    });

    if (fatalError) { return undefined; }

    if (!intersections.length) { return null; } // Nothing intersected.

    // Get a single XPolygon with everything combined.
    const mergedArray = mergeIntersectingPolygonsInArray(intersections);
    return polygonArrayToXPolygon(mergedArray);
  } catch (error) {
    workerLogError({error, context:{geometry1, geometry2}});
    return undefined;
  }
};

const _calculateOverlapClipperLib = (geometry1, geometry2) => clipperLibIntersect(geometry1, geometry2);

const _isXPolygonType = (geometryType) => geometryType === 'Polygon' || geometryType === 'MultiPolygon';

const _isNonAreaType = (geometryType) =>
  geometryType === 'Point' || geometryType === 'MultiPoint' || geometryType === 'LineString'
  || geometryType === 'MultiLineString';

// Written in "guarded" style.
const _getGeometryFromTurfResult = ({args, funcName, result}) => {
  try {
    if (result.type === 'GeometryCollection') { return result; }
    if (result.type === 'Feature' && result.geometry.type === 'GeometryCollection') { return result.geometry; } // Supporting Turf 3.x.
    return getGeometry(result);
  } catch (ignored) {
    logApiError({args, funcName, library:clippingLibrary.TURF, errorMessage:'returned non-geometry'});
    return undefined;
  }
};

/** @return Polygon or MultiPolygon containing overlap of geoJson1/2. Or null, if no overlap.
   Or undefined, if an error, in which case the error will already be logged. (guarded style) */
const _calculateOverlapTurf = (geometry1, geometry2, lenientHack) => {
  const funcName = 'intersect', library = clippingLibrary.TURF;
  const intersectionFeature = turfIntersect(getFeature(geometry1), getFeature(geometry2));
  if (!intersectionFeature) { return intersectionFeature; } // Covers null (successful non-intersection) and undefined (guarded call failed and error already logged.)

  const geometry = _getGeometryFromTurfResult(
    {result:intersectionFeature, args:[geometry1, geometry2], funcName});
  if (geometry === undefined) { return undefined; }

  // intersect() returned some kind of shape.

  // If intersection is valid XPolygon, then overlap (not just intersection) was found and can be returned.
  if (_isXPolygonType(geometry.type)) {
    if (!isValidXPolygon(geometry)) {
      logApiError({args:[geometry1, geometry2], funcName, library, errorMessage:'returned invalid XPolygon'});
      if (!lenientHack) { return undefined; }
    }
    if (!doesXPolygonHaveArea(geometry)) { return null; }
    return geometry;
  }

  // If intersection is GeometryCollection, it means intersect() found a collection of geometries that could not be
  // entirely represented with an area type. Filter out any non-area types (Point, LineString, etc)
  // and return an XPolygon with area if the remaining geometries can make one.
  if (geometry.type === 'GeometryCollection') {
    const polygons = geometryCollectionToArray(geometry).filter(forGeometry => _isXPolygonType(forGeometry.type));
    if (!polygons.length) { return null; } // Success, though there is likely some weird pre-existing geometry I'm comparing against.
    const xPolygon = polygonArrayToXPolygon(polygons);
    if (!isValidXPolygon(xPolygon)) {
      logApiError({args:[geometry1, geometry2], funcName, library,
        errorMessage:'returned GeometryCollection that generated invalid XPolygon.'});
      if (!lenientHack) { return undefined; }
      return undefined;
    }
    if (!doesXPolygonHaveArea(xPolygon)) { return null; }
    return xPolygon;
  }

  if (_isNonAreaType(geometry.type)) { return null; } // Success, though result may be unexpected to caller.

  // Whatever was returned couldn't be used.
  logApiError({args:[geometry1, geometry2], funcName, library, errorMessage:'returned unexpected geometry type'});
  return undefined;
};

/** Calculate the overlapping shape(s) between two geoJsons.
    @param geoJson1 Feature, Polygon, or MultiPolygon
    @param geoJson2 Feature, Polygon, or MultiPolygon
    @param useLibraries An array with each element indicating library to use for clipping operations. Will be tried in
       in order.
    @returns Polygon or MultiPolygon containing overlap of geoJson1/2. Or null, if no overlap. */
export const calculateOverlap = (geoJson1, geoJson2, useLibraries = DEFAULT_CLIPPING_LIBRARY) => {
  const geometry1 = roundGeometry(getGeometry(geoJson1));
  const geometry2 = roundGeometry(getGeometry(geoJson2));

  // Handle 2 trivial cases and also prevent errors in clipping functions.
  if (!doesXPolygonHaveArea(geometry1) || !doesXPolygonHaveArea(geometry2)) { return null; }

  // Aware of the absurdity of this, and hope to one day delete it down to one call to a trusted lib.
  let xPolygon;
  useLibraries.forEach(library => {
    if (xPolygon === undefined) {
      if (library === 'MFOGEL') {
        const coordinates = cglIntersect(workerLogError, geometry1.coordinates, geometry2.coordinates);
        if (coordinates.length === 0) {
          xPolygon = null;
        } else if (coordinates.length === 1) {
          xPolygon = { type: 'Polygon', coordinates: coordinates[0] };
        } else {
          xPolygon = { type: 'MultiPolygon', coordinates };
        }
      }
      if (library === clippingLibrary.TURF) {
        xPolygon = _calculateOverlapTurf(geometry1, geometry2, true);
      } else if (library === clippingLibrary.MARTINEZ) {
        xPolygon = _calculateOverlapMartinez(geometry1, geometry2);
      } else if (library === clippingLibrary.CLIPPERLIB) {
        xPolygon = _calculateOverlapClipperLib(geometry1, geometry2);
      }
    }
  });

  if (xPolygon !== undefined) { return xPolygon; }

  throw new Error('calculateOverlap() failed'); // Specific reason already logged.
};

export const doPolygonsOverlap = (
  {polygon1, polygon2, ignoreThreshold = 0, useLibraries = DEFAULT_CLIPPING_LIBRARY}) => {
  const overlap = calculateOverlap(polygon1, polygon2, useLibraries);
  if (overlap === null) { return false; }
  if (ignoreThreshold === 0) { return true; }

  // Check for a ignorable area of overlap.
  const overlapArea = area(overlap);
  return (overlapArea > ignoreThreshold);
};

export const doPolygonsIntersect = ({ polygon1, polygon2 }) => {
  // It would be much simpler code, but don't use Turf's intersect() until they fix it.

  const featureCollection = lineIntersect(polygon1, polygon2);
  if (featureCollection === undefined) { throw Error('lineIntersect failed'); } // Already logged to Scarecrow.
  if (get(featureCollection, 'features.length', 0) > 0) { return true; }

  // Perform the more expensive check for a polygon inside of a polygon (no intersecting lines between them).
  return doPolygonsOverlap({
    polygon1,
    polygon2,
    useLibraries: ['MFOGEL']
  });
};

export const doAnyPolygonsInArrayIntersect = ({polygons, useLibraries = DEFAULT_CLIPPING_LIBRARY}) => // TODO - 3/10/16 Currently not referenced, but may still use. If you see this here in 6 months and still not referenced by any code, just delete it.
  polygons.some((polygon1, leftIndex) =>
    polygons.some((polygon2, rightIndex) =>
      rightIndex > leftIndex && doPolygonsIntersect({polygon1, polygon2, useLibraries})
    )
  );

export const doAnyPolygonsInArrayOverlap = (
  {polygons, ignoreThreshold = 0, useLibraries = DEFAULT_CLIPPING_LIBRARY}) =>
  polygons.some((polygon1, leftIndex) =>
    polygons.some((polygon2, rightIndex) =>
      rightIndex > leftIndex && doPolygonsOverlap({polygon1, polygon2, ignoreThreshold, useLibraries})
    )
  );

/** Purposefully, does not check for overlap of a polygon with other polygons in its same array. */
export const doAnyPolygonsInArraysOverlap = (
  {polygons1, polygons2, ignoreThreshold = 0, useLibraries = DEFAULT_CLIPPING_LIBRARY}) =>
  polygons1.some((polygon1) =>
    polygons2.some((polygon2) =>
      doPolygonsOverlap({polygon1, polygon2, ignoreThreshold, useLibraries})
    )
  );

export const unitTestPrivate = {
  _calculateOverlapMartinez,
  _calculateOverlapTurf
};
